import { __decorate } from "tslib";
import { LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { HTTP_STATUS_CODE } from './enums/httpstatuscode.js';
import { getTimeoutInfo, storeActivityTime, storeUserActivityInfo, } from './helpers/userActivityHelpers.js';
import { heartbeatRepository } from './repositories/heartBeatRepository.js';
import { UserActivityTracker } from './helpers/userActivityTracker.js';
const USER_LOGOUT_MESSAGE = 'user-logout';
let ViedocInactivityTrackerCrossDomain = class ViedocInactivityTrackerCrossDomain extends LitElement {
    constructor() {
        super();
        /*
        The URL to which the inactivity-timeout component will redirect the browser when inactivity timeout.
        If the logoutCallback is available, it will use the logoutCallback instead of this.
         */
        this.postLogoutUri = '';
        this.heartBeatUrl = '';
        this.isSinglePage = false;
        this.keepAlive = false;
        this.userActivityTrackingIntervalTime = 2 * 1000; // 2 seconds
        this.keepUserActiveIntervalTime = 1 * 1000 * 60; // 1 minute
        this.isUserActive = false;
        this.timeoutId = null;
        this.scheduleId = null;
        this.isChangingTab = false;
        this.userActivityTracker = new UserActivityTracker(this.userActivityTrackingIntervalTime, this.trackUserActivityAsync.bind(this), this.updateWhenUserActive.bind(this), this.windowFocus.bind(this));
    }
    connectedCallback() {
        super.connectedCallback();
        this.userActivityTracker.init();
        this.inactivityTrackerChannel = new BroadcastChannel('inactivity-tracker-channel');
        this.inactivityTrackerChannel.onmessage =
            this.handleBroadcastMessage.bind(this);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.clearInterval(this.keepUserActiveInterval);
        this.userActivityTracker.clear();
        this.clearSchedulePing();
        if (this.inactivityTrackerChannel) {
            this.inactivityTrackerChannel.close();
        }
    }
    updated() {
        if (this.keepAlive) {
            if (!this.keepUserActiveInterval) {
                this.keepUserActiveInterval = window.setInterval(() => {
                    storeActivityTime();
                }, this.keepUserActiveIntervalTime);
            }
        }
        else {
            window.clearInterval(this.keepUserActiveInterval);
            this.keepUserActiveInterval = undefined;
        }
    }
    async firstUpdated() {
        storeActivityTime(); // Store the current time as the user's last activity time
        await this.ping(); // Ping the server to check the global last activity on the server side.
    }
    /* Tracking user activities */
    // eslint-disable-next-line class-methods-use-this
    storeUserActivityTime() {
        storeActivityTime();
    }
    async trackUserActivityAsync() {
        if (this.isUserActive) {
            storeActivityTime();
            await this.scheduleNextPing();
            // Reset until user's next action
            this.isUserActive = false;
        }
    }
    async ping() {
        // When init the component, the tokenCallBack is not provided yet. So that need to delay until it's available.
        let response;
        if (this.isSinglePage) {
            if (!this.tokenCallback)
                return;
            const accessToken = this.tokenCallback();
            response = await heartbeatRepository.sendHeartBeat(this.heartBeatUrl, accessToken);
        }
        else {
            response = await heartbeatRepository.sendHeartBeat('/api/heartbeat');
        }
        await this.handleHeartBeatResponse(response);
    }
    async handleHeartBeatResponse(response) {
        if (response) {
            if (response.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
                await this.postMessageLogout();
                return;
            }
            if (response.ok) {
                // When a tab is changing, there are two cases that we need to care
                // case-1: user is logout from the other tab -> if somehow the ping is not reachout to the server we need to do it again immediately
                // case-2: timeout is very close (less than a ping-interval) we need to sync the lastactivity immediately.
                // so that we turn off this flag only when we make sure that the server is received the last activity
                // or the current tab get the UNAUTHORIZED from the server by the above check.
                this.isChangingTab = false;
                storeUserActivityInfo(await response.json());
            }
        }
        // Avoid recursive & too many ping when error/network issue.
        this.clearSchedulePing();
        this.scheduleId = setTimeout(async () => {
            await this.scheduleNextPing();
        }, this.userActivityTrackingIntervalTime);
        // 2 seconds. keep the same with the way of detect active user interval.
        // that means if user is active and lostnetwork/error will be a ping per two-second.
    }
    updateWhenUserActive() {
        if (this.isUserActive) {
            return;
        }
        this.isUserActive = true;
    }
    windowFocus() {
        this.isChangingTab = true;
    }
    clearSchedulePing() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (this.scheduleId) {
            clearTimeout(this.scheduleId);
            this.scheduleId = null;
        }
    }
    async scheduleNextPing() {
        if (this.isChangingTab) {
            this.clearSchedulePing();
            await this.ping();
            return;
        }
        const timeoutInfo = getTimeoutInfo();
        if (!timeoutInfo)
            return;
        if (timeoutInfo.isGlobalBehindLocal()) {
            this.clearSchedulePing();
            await this.ping();
            return;
        }
        // if there is a scheduled ping and we don't need to ping immediately
        // just ignore
        if (!this.timeoutId) {
            this.timeoutId = setTimeout(async () => {
                await this.ping();
            }, timeoutInfo.getNextPingInterval());
        }
    }
    /* End user tracking activities */
    async postMessageLogout() {
        // Use the broadcast channel to notify other tabs to log out immediately.
        this.inactivityTrackerChannel.postMessage(USER_LOGOUT_MESSAGE);
        await this.logout();
    }
    async logout() {
        if (this.logoutCallback) {
            await this.logoutCallback();
        }
        else {
            const firefoxBrowser = navigator.userAgent
                .toLowerCase()
                .includes('firefox');
            if (!firefoxBrowser || this.isSinglePage) {
                window.location.href = this.postLogoutUri;
            }
            else {
                const response = await fetch(this.postLogoutUri);
                if (response.ok) {
                    window.location.reload();
                }
            }
        }
    }
    async handleBroadcastMessage(event) {
        if (event.data === USER_LOGOUT_MESSAGE) {
            await this.logout();
        }
    }
};
__decorate([
    property({ type: String, attribute: 'post-logout-uri' })
], ViedocInactivityTrackerCrossDomain.prototype, "postLogoutUri", void 0);
__decorate([
    property({ attribute: false })
], ViedocInactivityTrackerCrossDomain.prototype, "logoutCallback", void 0);
__decorate([
    property({ type: String, attribute: 'heartbeat-url' })
], ViedocInactivityTrackerCrossDomain.prototype, "heartBeatUrl", void 0);
__decorate([
    property({ attribute: false })
], ViedocInactivityTrackerCrossDomain.prototype, "tokenCallback", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-single-page' })
], ViedocInactivityTrackerCrossDomain.prototype, "isSinglePage", void 0);
__decorate([
    property({ type: Boolean, attribute: 'keep-alive' })
], ViedocInactivityTrackerCrossDomain.prototype, "keepAlive", void 0);
__decorate([
    property({ type: Number, attribute: 'user-activity-tracking-interval' })
], ViedocInactivityTrackerCrossDomain.prototype, "userActivityTrackingIntervalTime", void 0);
__decorate([
    property({ type: Number, attribute: 'keep-user-active-interval' })
], ViedocInactivityTrackerCrossDomain.prototype, "keepUserActiveIntervalTime", void 0);
ViedocInactivityTrackerCrossDomain = __decorate([
    customElement('viedoc-inactivity-tracker-cross-domain')
], ViedocInactivityTrackerCrossDomain);
export { ViedocInactivityTrackerCrossDomain };
